import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Alert,
  Switch,
  FormControlLabel,
  Tooltip,
  Chip,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import { Add, Edit, Delete, Send } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { ValidatedTextField } from '../components/ui/ValidatedTextField';
import { validateEmail } from '../utils/inputValidators';
import {
  ContentScope,
  CONTENT_SCOPE_DESCRIPTIONS,
  CONTENT_SCOPE_LABELS,
  CONTENT_SCOPE_ORDER,
} from '../hooks/useContentScopes';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { personDisplayName } from '../utils/orgDisplayName';
import {
  ConfirmDialog,
  DataTableShell,
  EmptyState,
  FormDialog,
  LoadingState,
  PageHeader,
  SectionCard,
  StatusChip,
  statusTone,
  statusLabel,
  PageContainer,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';
import {
  fetchStoreLocationOptions,
  updateMemberLocations,
  StoreLocationSummary,
} from '../services/storeTeamApi';

/** Owners and admins always see every location and cannot be assigned. */
const UNSCOPED_ROLES = ['owner', 'admin', 'super_admin'];

/** "owner" -> "Owner", "super_admin" -> "Super admin". */
const roleLabel = (role?: string): string => {
  const text = (role || '').replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

const PRODUCT_QA_TOOLTIP = 'Lets this member review and correct product data in the Product QA queue.';
const REMOVE_OWNER_TOOLTIP = "The owner can't be removed. Transfer ownership in Store Settings first.";

/** Small uppercase faint label above a mobile card's value, matching the ResponsiveRows mobile cell label. */
const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
  mb: '4px',
};

interface TeamMember {
  userId?: string;
  user_id?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status?: string;
  productQaEnabled?: boolean;
  contentScopes?: ContentScope[];
  /** Store locations this member works at; empty means all of them */
  locationIds?: string[];
  invitedAt?: Date;
  lastActiveAt?: Date;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invitedAt?: string;
  expiresAt?: string;
}

export const TeamManagementPage: React.FC = () => {
  const { userProfile, isAdmin } = useAuth();
  const { isImpersonating, impersonatedOrg, getEffectiveUserType } = useImpersonation();
  const isMobile = useIsMobile();
  const effectiveUserType = getEffectiveUserType(userProfile);
  const isStoreOrg = effectiveUserType === 'store';
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingMember, setEditingMember] = React.useState<TeamMember | null>(null);
  const [editRole, setEditRole] = React.useState('');
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('employee');
  const [inviteLocationIds, setInviteLocationIds] = React.useState<string[]>([]);
  const { showToast } = useToast();
  const [removeTarget, setRemoveTarget] = React.useState<TeamMember | null>(null);
  const [revokeTarget, setRevokeTarget] = React.useState<PendingInvitation | null>(null);

  // Get organization ID - use impersonated org if impersonating, otherwise user's org
  const organizationId = isImpersonating && impersonatedOrg?.id 
    ? impersonatedOrg.id 
    : (userProfile?.organizationId || (userProfile as any)?.primaryOrganizationId);

  // Fetch organization members
  const { data: membersData, isLoading } = useQuery({
    queryKey: ['organization-members', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      const response = await makeApiCall(`/store/members`, {
        headers: await getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch organization members');
      }
      return response.json();
    },
    enabled: !!organizationId,
  });

  // Transform the API response to match TeamMember interface
  const members: TeamMember[] = React.useMemo(() => {
    const rawMembers = membersData?.data || [];
    return rawMembers.map((member: any) => ({
      userId: member.userId || member.user_id || member.id,
      email: member.email,
      displayName: member.displayName || member.name,
      firstName: member.firstName,
      lastName: member.lastName,
      role: member.role || 'user',
      status: member.status || 'active',
      productQaEnabled: member.productQaEnabled === true,
      contentScopes: Array.isArray(member.contentScopes) ? member.contentScopes : [],
      locationIds: Array.isArray(member.locationIds) ? member.locationIds : [],
      invitedAt: member.invitedAt,
      lastActiveAt: member.lastActiveAt,
    }));
  }, [membersData]);

  // The store's locations, so a member can be assigned to some of them
  const { data: locations = [] } = useQuery<StoreLocationSummary[]>({
    queryKey: ['store-locations', organizationId],
    queryFn: fetchStoreLocationOptions,
    enabled: !!organizationId,
  });

  const locationsMutation = useMutation({
    mutationFn: async ({ userId, locationIds }: { userId: string; locationIds: string[] }) => {
      if (!organizationId) throw new Error('No organization found');
      await updateMemberLocations(organizationId, userId, locationIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      showToast('Stores updated.', 'success');
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to update stores', 'error');
    },
  });

  const inviteMutation = useMutation({
    mutationFn: async ({ email, role, locationIds }: { email: string; role: string; locationIds: string[] }) => {
      const orgId = organizationId || userProfile?.organizationId || (userProfile as any)?.primaryOrganizationId;
      if (!orgId) {
        throw new Error('No organization found');
      }
      const response = await makeApiCall(`/organizations/${orgId}/invite`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        // Empty means every location; owners and admins are never scoped
        body: JSON.stringify({ email, role, locationIds: UNSCOPED_ROLES.includes(role) ? [] : locationIds }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to send invitation' }));
        throw new Error(errorData.message || 'Failed to send invitation');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      showToast('Invitation sent successfully!', 'success');
      setDialogOpen(false);
      setInviteEmail('');
      setInviteRole('employee');
      setInviteLocationIds([]);
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to send invitation', 'error');
    },
  });

  // Who has been invited and hasn't joined yet, plus resend/revoke for it —
  // the invite dialog above only sends new ones.
  const { data: invitationsData, isLoading: invitationsLoading } = useQuery({
    queryKey: ['pending-invitations', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      const response = await makeApiCall(`/invitations/organization/${organizationId}`, {
        headers: await getAuthHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error?.message || body?.message || 'Failed to load invitations');
      }
      return body;
    },
    enabled: !!organizationId,
  });

  const pendingInvitations: PendingInvitation[] = (invitationsData?.data || []).filter(
    (invitation: PendingInvitation) => invitation.status === 'pending'
  );

  const resendInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await makeApiCall(`/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: await getAuthHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error?.message || body?.message || 'Failed to resend invitation');
      }
      return body;
    },
    onSuccess: () => {
      showToast('Invitation resent.', 'success');
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to resend invitation', 'error');
    },
  });

  const revokeInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await makeApiCall(`/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error?.message || body?.message || 'Failed to revoke invitation');
      }
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invitations', organizationId] });
      showToast('Invitation revoked.', 'success');
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to revoke invitation', 'error');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const orgId = organizationId;
      if (!orgId) {
        throw new Error('No organization found');
      }
      const response = await makeApiCall(`/organizations/${orgId}/users/${userId}`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ role }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update role' }));
        throw new Error(errorData.message || 'Failed to update role');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      showToast('Role updated successfully!', 'success');
      setEditDialogOpen(false);
      setEditingMember(null);
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to update role', 'error');
    },
  });

  /**
   * Per-action grants. Sent as the whole array the member should end up with,
   * so the server stores an explicit set and stops falling back to the legacy
   * all-or-nothing bundle for this user.
   */
  const contentScopesMutation = useMutation({
    mutationFn: async ({ userId, scopes }: { userId: string; scopes: ContentScope[] }) => {
      const orgId = organizationId;
      if (!orgId) {
        throw new Error('No organization found');
      }
      const response = await makeApiCall(`/organizations/${orgId}/users/${userId}`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ contentScopes: scopes }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update permissions' }));
        throw new Error(errorData.message || 'Failed to update permissions');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      showToast('Permissions updated.', 'success');
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to update permissions', 'error');
    },
  });

  const qaAccessMutation = useMutation({
    mutationFn: async ({ userId, enabled }: { userId: string; enabled: boolean }) => {
      const orgId = organizationId;
      if (!orgId) {
        throw new Error('No organization found');
      }
      const response = await makeApiCall(`/organizations/${orgId}/users/${userId}`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ productQaEnabled: enabled }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update QA access' }));
        throw new Error(errorData.message || 'Failed to update QA access');
      }
      return response.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      showToast(variables.enabled ? 'Product QA access granted.' : 'Product QA access revoked.', 'success');
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to update QA access', 'error');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      const orgId = organizationId;
      if (!orgId) {
        throw new Error('No organization found');
      }
      const response = await makeApiCall(`/organizations/${orgId}/users/${userId}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to remove member' }));
        throw new Error(errorData.message || 'Failed to remove member');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
      showToast('Member removed successfully!', 'success');
    },
    onError: (error) => {
      showToast(error instanceof Error ? error.message : 'Failed to remove member', 'error');
    },
  });

  const handleInvite = () => {
    if (!inviteEmail || !organizationId) return;
    inviteMutation.mutate({ email: inviteEmail, role: inviteRole, locationIds: inviteLocationIds });
  };

  const locationName = (id: string) => locations.find((l) => l.id === id)?.name || id;

  const handleEditClick = (member: TeamMember) => {
    setEditingMember(member);
    setEditRole(member.role);
    setEditDialogOpen(true);
  };

  const handleUpdateRole = () => {
    if (!editingMember?.userId || !editRole) return;
    updateRoleMutation.mutate({ userId: editingMember.userId, role: editRole });
  };

  const handleRemoveMember = (member: TeamMember) => {
    if (!member.userId) return;
    setRemoveTarget(member);
  };

  const getRoleTone = (role: string) => {
    return ['admin', 'super_admin'].includes(role) ? 'problem' : 'neutral';
  };

  const getUserDisplayName = (member: TeamMember) => personDisplayName(member, 'Unknown User');

  return (
    <PageContainer>
      <PageHeader
        title="Team Management"
        description="Invite and manage your organization members."
        actionLabel="Invite Member"
        actionIcon={<Add />}
        onAction={() => setDialogOpen(true)}
      />

      {isLoading ? (
        <LoadingState message="Loading organization members..." />
      ) : members.length === 0 ? (
        <EmptyState
          title="No team members yet"
          description="Invite someone to get started with your organization."
          actionLabel="Invite Member"
          onAction={() => setDialogOpen(true)}
        />
      ) : isMobile ? (
        <Paper sx={{ borderRadius: 2 }}>
          <Stack divider={<Divider />}>
            {members.map((member, index) => (
              <Box key={member.userId || member.email || index} sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, flexShrink: 0 }}>
                      {(getUserDisplayName(member) || member.email || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {getUserDisplayName(member) || member.displayName || member.email || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ wordBreak: 'break-all' }}>
                        {member.email || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <StatusChip label={roleLabel(member.role)} tone={getRoleTone(member.role)} size="small" />
                    <StatusChip
                      label={statusLabel(member.status || 'active')}
                      tone={statusTone(member.status || 'active')}
                      size="small"
                    />
                  </Stack>
                </Stack>

                {isStoreOrg && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography component="span" sx={mobileLabelSx}>Stores</Typography>
                    {UNSCOPED_ROLES.includes(member.role) ? (
                      <Typography variant="body2" color="text.secondary">All stores</Typography>
                    ) : (
                      <Select
                        multiple
                        fullWidth
                        size="small"
                        displayEmpty
                        value={member.locationIds || []}
                        disabled={!member.userId || locationsMutation.isPending || locations.length === 0}
                        onChange={(e) => {
                          if (!member.userId) return;
                          const value = e.target.value;
                          locationsMutation.mutate({
                            userId: member.userId,
                            locationIds: typeof value === 'string' ? value.split(',') : (value as string[]),
                          });
                        }}
                        renderValue={(selected) =>
                          (selected as string[]).length === 0
                            ? 'All stores'
                            : (selected as string[]).map(locationName).join(', ')
                        }
                        inputProps={{ 'aria-label': `Stores for ${getUserDisplayName(member)}` }}
                      >
                        {locations.map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Box>
                )}

                {isAdmin && isStoreOrg && (
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography component="span" sx={{ ...mobileLabelSx, mb: 0 }}>Product QA</Typography>
                    <Tooltip title={PRODUCT_QA_TOOLTIP}>
                      <Switch
                        size="small"
                        checked={member.productQaEnabled === true}
                        onChange={(e) => {
                          if (!member.userId) return;
                          qaAccessMutation.mutate({ userId: member.userId, enabled: e.target.checked });
                        }}
                        disabled={!member.userId || qaAccessMutation.isPending}
                        inputProps={{ 'aria-label': 'Toggle Product QA access' }}
                      />
                    </Tooltip>
                  </Box>
                )}

                {isAdmin && isStoreOrg && member.productQaEnabled === true && (
                  <Box sx={{ mt: 1 }}>
                    <Typography component="span" sx={mobileLabelSx}>Permissions</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {CONTENT_SCOPE_ORDER.map((scope) => {
                        const on = (member.contentScopes || []).includes(scope);
                        return (
                          <Tooltip key={scope} title={CONTENT_SCOPE_DESCRIPTIONS[scope]}>
                            <Chip
                              size="small"
                              label={CONTENT_SCOPE_LABELS[scope]}
                              color={on ? 'primary' : 'default'}
                              variant={on ? 'filled' : 'outlined'}
                              disabled={!member.userId || contentScopesMutation.isPending}
                              onClick={() => {
                                if (!member.userId) return;
                                const current = member.contentScopes || [];
                                contentScopesMutation.mutate({
                                  userId: member.userId,
                                  scopes: on
                                    ? current.filter((sc) => sc !== scope)
                                    : [...current, scope],
                                });
                              }}
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                  <Button size="small" startIcon={<Edit />} onClick={() => handleEditClick(member)}>
                    Edit
                  </Button>
                  <Tooltip title={member.role === 'owner' ? REMOVE_OWNER_TOOLTIP : ''}>
                    <span>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleRemoveMember(member)}
                        disabled={member.role === 'owner'}
                      >
                        Remove
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : (
        <DataTableShell>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: tokens.surface.sunken }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Member</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  {isStoreOrg && (
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="Which of your stores this member works at. Owners and admins always see every store.">
                        <span>Stores</span>
                      </Tooltip>
                    </TableCell>
                  )}
                  {isAdmin && isStoreOrg && (
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Tooltip title={PRODUCT_QA_TOOLTIP}>
                        <span>Product QA</span>
                      </Tooltip>
                    </TableCell>
                  )}
                  {isAdmin && isStoreOrg && (
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="What this member can do in the Product QA queue. Filled means granted, outlined means not granted. Platform admins hold all of these.">
                        <span>Permissions</span>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member, index) => (
                  <TableRow key={member.userId || member.email || index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          {(getUserDisplayName(member) || member.email || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">
                          {getUserDisplayName(member) || member.displayName || member.email || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{member.email || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip label={roleLabel(member.role)} tone={getRoleTone(member.role)} size="small" />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={statusLabel(member.status || 'active')}
                        tone={statusTone(member.status || 'active')}
                        size="small"
                      />
                    </TableCell>
                    {isStoreOrg && (
                      <TableCell>
                        {UNSCOPED_ROLES.includes(member.role) ? (
                          <Typography variant="caption" color="text.secondary">All stores</Typography>
                        ) : (
                          <Select
                            multiple
                            size="small"
                            displayEmpty
                            value={member.locationIds || []}
                            disabled={!member.userId || locationsMutation.isPending || locations.length === 0}
                            onChange={(e) => {
                              if (!member.userId) return;
                              const value = e.target.value;
                              locationsMutation.mutate({
                                userId: member.userId,
                                locationIds: typeof value === 'string' ? value.split(',') : (value as string[]),
                              });
                            }}
                            renderValue={(selected) =>
                              (selected as string[]).length === 0
                                ? 'All stores'
                                : (selected as string[]).map(locationName).join(', ')
                            }
                            inputProps={{ 'aria-label': `Stores for ${getUserDisplayName(member)}` }}
                          >
                            {locations.map((location) => (
                              <MenuItem key={location.id} value={location.id}>
                                {location.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>
                    )}
                    {isAdmin && isStoreOrg && (
                      <TableCell>
                        <Tooltip title={PRODUCT_QA_TOOLTIP}>
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={member.productQaEnabled === true}
                                onChange={(e) => {
                                  if (!member.userId) return;
                                  qaAccessMutation.mutate({ userId: member.userId, enabled: e.target.checked });
                                }}
                                disabled={!member.userId || qaAccessMutation.isPending}
                                inputProps={{ 'aria-label': 'Toggle Product QA access' }}
                              />
                            }
                            label={<Typography variant="caption" color="text.secondary">{member.productQaEnabled === true ? 'Can review' : 'Off'}</Typography>}
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    {isAdmin && isStoreOrg && (
                      <TableCell>
                        {member.productQaEnabled !== true ? (
                          <Typography variant="caption" color="text.secondary">
                            Product QA is off
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {CONTENT_SCOPE_ORDER.map((scope) => {
                              const on = (member.contentScopes || []).includes(scope);
                              return (
                                <Tooltip key={scope} title={CONTENT_SCOPE_DESCRIPTIONS[scope]}>
                                  <Chip
                                    size="small"
                                    label={CONTENT_SCOPE_LABELS[scope]}
                                    color={on ? 'primary' : 'default'}
                                    variant={on ? 'filled' : 'outlined'}
                                    disabled={!member.userId || contentScopesMutation.isPending}
                                    onClick={() => {
                                      if (!member.userId) return;
                                      const current = member.contentScopes || [];
                                      contentScopesMutation.mutate({
                                        userId: member.userId,
                                        scopes: on
                                          ? current.filter((sc) => sc !== scope)
                                          : [...current, scope],
                                      });
                                    }}
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          size="small" 
                          startIcon={<Edit />}
                          onClick={() => handleEditClick(member)}
                        >
                          Edit
                        </Button>
                        <Tooltip title={member.role === 'owner' ? REMOVE_OWNER_TOOLTIP : ''}>
                          <span>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleRemoveMember(member)}
                              disabled={member.role === 'owner'}
                            >
                              Remove
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {isAdmin && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1.5 }}>
              Product QA permissions: a filled chip means the member has that permission, an outlined chip means they don't. Select a chip to switch it.
            </Typography>
          )}
        </DataTableShell>
      )}

      <SectionCard dense title="Pending Invitations" subtitle="Invited, not yet accepted." sx={{ mt: 3 }}>
        {invitationsLoading ? (
          <LoadingState message="Loading invitations..." />
        ) : pendingInvitations.length === 0 ? (
          <EmptyState size="compact" title="No pending invitations" description="Everyone you've invited has joined, or nothing is outstanding." />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: tokens.surface.sunken }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingInvitations.map((invitation) => (
                  <TableRow key={invitation.id} hover>
                    <TableCell>
                      <Typography variant="body2">{invitation.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip label={roleLabel(invitation.role)} tone={getRoleTone(invitation.role)} size="small" />
                    </TableCell>
                    <TableCell>
                      <StatusChip label={statusLabel(invitation.status)} tone={statusTone(invitation.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<Send />}
                          disabled={resendInvitationMutation.isPending}
                          onClick={() => resendInvitationMutation.mutate(invitation.id)}
                        >
                          Resend
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => setRevokeTarget(invitation)}
                        >
                          Revoke
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>

      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleInvite}
        title="Invite Team Member"
        submitLabel="Send Invite"
        submitDisabled={!inviteEmail}
      >
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <ValidatedTextField
              label="Email Address"
              type="email"
              fullWidth
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              validate={validateEmail}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteRole}
                label="Role"
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            {!UNSCOPED_ROLES.includes(inviteRole) && locations.length > 0 && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Stores</InputLabel>
                <Select
                  multiple
                  label="Stores"
                  value={inviteLocationIds}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInviteLocationIds(typeof value === 'string' ? value.split(',') : (value as string[]));
                  }}
                  renderValue={(selected) =>
                    (selected as string[]).length === 0
                      ? 'All stores'
                      : (selected as string[]).map(locationName).join(', ')
                  }
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
      </FormDialog>

      {/* Edit Member Dialog */}
      <FormDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={handleUpdateRole}
        title="Edit Team Member"
        submitLabel={updateRoleMutation.isPending ? 'Updating...' : 'Update Role'}
        submitDisabled={editingMember?.role === 'owner' || updateRoleMutation.isPending}
      >
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Editing: <strong>{editingMember?.displayName || editingMember?.email}</strong>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editRole}
                label="Role"
                onChange={(e) => setEditRole(e.target.value)}
                disabled={editingMember?.role === 'owner'}
              >
                <MenuItem value="owner">Owner</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </Select>
            </FormControl>
            {editingMember?.role === 'owner' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Owner role cannot be changed directly. Use Transfer Ownership to change the owner.
              </Alert>
            )}
          </Box>
        </DialogContent>
      </FormDialog>

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove Member"
        message={`Are you sure you want to remove ${removeTarget?.displayName || removeTarget?.email || 'this member'} from the organization?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={removeMemberMutation.isPending}
        onConfirm={() => {
          if (removeTarget?.userId) {
            removeMemberMutation.mutate(removeTarget.userId, { onSettled: () => setRemoveTarget(null) });
          }
        }}
        onClose={() => setRemoveTarget(null)}
      />

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke Invitation"
        message={`Are you sure you want to revoke the invitation sent to ${revokeTarget?.email || 'this address'}? The link in their email will stop working.`}
        confirmLabel="Revoke"
        confirmColor="error"
        loading={revokeInvitationMutation.isPending}
        onConfirm={() => {
          if (revokeTarget?.id) {
            revokeInvitationMutation.mutate(revokeTarget.id, { onSettled: () => setRevokeTarget(null) });
          }
        }}
        onClose={() => setRevokeTarget(null)}
      />

    </PageContainer>
  );
};

export default TeamManagementPage;

